'use client'

import { useEffect, useRef, useState } from 'react'
import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompleteStatus
} from '@/components/reui/autocomplete'

import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { Mid } from '@modules/manifera-user/resources/js/pages/page-mid-overview/schema'
import client from '@/http/client'
import { cn } from '@/lib/utils'
import { Button } from './ui/button'

async function searchMids(
  query: string,
  apiUrl = '/api/mids/autocomplete',
  fieldName?: string,
  relatedFieldValue?: string
): Promise<Mid[]> {
  const response = await client.get<Mid[]>(apiUrl, {
    params: {
      q: query,
      ...(fieldName && relatedFieldValue
        ? { [fieldName]: relatedFieldValue }
        : {})
    }
  })

  return response.data
}

interface MIDsAutocompleteProps {
  searchValue: string
  setSearchValue: (value: string) => void
  onSelect?: (mid?: Mid) => void
  containerClassName?: string
  inputClassName?: string
  dropdownClassName?: string
  itemClassName?: string
  displayValue?: (mid: Mid) => string
  placeholder?: string
  style?: React.CSSProperties
  apiUrl?: string
  fieldName?: string
  isCreatedMid?: boolean
  onCreated?: () => void
  onClearCreated?: () => void
  onPendingChange?: (isPending: boolean) => void
  clearMessageTrigger?: number
  hasValidMid?: boolean
  relatedFieldValue?: string
  relatedFieldName?: string
}

export function MIDsAutocomplete({
  searchValue,
  setSearchValue,
  onSelect,
  containerClassName,
  inputClassName,
  dropdownClassName,
  itemClassName,
  displayValue = (mid) => mid.brand,
  placeholder,
  style,
  apiUrl,
  fieldName = 'merk',
  isCreatedMid = false,
  onCreated,
  onClearCreated,
  onPendingChange,
  hasValidMid = false,
  relatedFieldValue,
  relatedFieldName
}: MIDsAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const [_selectedMid, setSelectedMid] = useState<Mid | null>(null)
  const hasSelectedRef = useRef(searchValue.trim().length > 0)
  const previousHasValidMidRef = useRef(hasValidMid)
  // Tracks whether an action (select/create) was triggered — used to cancel blur auto-create
  const actionFiredRef = useRef(false)
  // Ref mirrors for use inside setTimeout callbacks
  const searchValueRef = useRef(searchValue)
  const fieldNameRef = useRef(fieldName)
  const isCreatedMidRef = useRef(isCreatedMid)

  useEffect(() => {
    searchValueRef.current = searchValue
  }, [searchValue])
  useEffect(() => {
    fieldNameRef.current = fieldName
  }, [fieldName])
  useEffect(() => {
    isCreatedMidRef.current = isCreatedMid
  }, [isCreatedMid])

  useEffect(() => {
    if (hasValidMid && !previousHasValidMidRef.current) {
      setOpen(false)
      hasSelectedRef.current = true
    }

    previousHasValidMidRef.current = hasValidMid
  }, [hasValidMid])

  const [debouncedValue, setDebouncedValue] = useState(searchValue)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(searchValue)
    }, 300)

    return () => {
      clearTimeout(timer)
    }
  }, [searchValue])

  const isEnabled = debouncedValue.trim().length >= 1

  const { data: searchResults = [] } = useQuery({
    queryKey: [
      'mids',
      'autocomplete',
      debouncedValue,
      apiUrl,
      relatedFieldName,
      relatedFieldValue
    ],
    queryFn: () =>
      searchMids(debouncedValue, apiUrl, relatedFieldName, relatedFieldValue),
    enabled: isEnabled
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    if (newValue.trim().length === 0) {
      actionFiredRef.current = true // cancel any pending blur handler
      hasSelectedRef.current = false
      setSelectedMid(null)
      onPendingChange?.(false)

      if (isCreatedMid) onClearCreated?.()

      onSelect?.(undefined)
      setSearchValue(newValue)
      return
    }

    if (hasSelectedRef.current) {
      hasSelectedRef.current = false
      setSelectedMid(null)
      onPendingChange?.(true)
      onSelect?.(undefined)
    }

    if (isCreatedMid) {
      onClearCreated?.()
      onPendingChange?.(true)
    }

    setSearchValue(newValue)
  }

  const handleBlur = () => {
    // Defer so that mousedown on dropdown items / buttons fires first.
    // If an action (select or create) fires in that window, actionFiredRef
    // will be true and we skip the auto-create.
    actionFiredRef.current = false

    setTimeout(() => {
      if (actionFiredRef.current) {
        // User clicked a list item or "Toevoegen" button — don't auto-create
        actionFiredRef.current = false
        return
      }

      const currentValue = searchValueRef.current
      const currentIsCreatedMid = isCreatedMidRef.current

      if (
        currentValue &&
        currentValue.trim().length > 0 &&
        !hasSelectedRef.current &&
        !currentIsCreatedMid
      ) {
        // Auto-treat as "Toevoegen" on blur
        setOpen(false)
        hasSelectedRef.current = false
        onPendingChange?.(false)
        onSelect?.(undefined)
        onCreated?.()
      }
    }, 200)
  }

  const handleSelectMid = (mid: Mid) => {
    actionFiredRef.current = true

    onSelect?.({
      ...mid,
      model: mid.model ?? ''
    })

    setSelectedMid(mid)
    hasSelectedRef.current = true
    onPendingChange?.(false)

    if (isCreatedMid) {
      onClearCreated?.()
    }

    setOpen(false)
  }

  const handleCreateMid = () => {
    actionFiredRef.current = true

    setOpen(false)
    hasSelectedRef.current = false
    setSelectedMid(null)
    onPendingChange?.(false)
    onSelect?.(undefined)
    onCreated?.()
  }

  const shouldShowDropdown = open && searchValue !== ''

  return (
    <div className={cn('w-full max-w-xs', containerClassName)} style={style}>
      <Autocomplete
        items={searchResults}
        value={searchValue}
        onValueChange={setSearchValue}
        openOnInputClick
        itemToStringValue={displayValue}
        filter={null}
        open={open}
        onOpenChange={setOpen}
      >
        <AutocompleteInput
          placeholder={placeholder ?? 'MIDs zoeken...'}
          showClear
          onChange={handleInputChange}
          onBlur={handleBlur}
          size='lg'
          className={cn(inputClassName)}
          autoComplete=''
        />

        {shouldShowDropdown && (
          <AutocompleteContent>
            <AutocompleteList className={cn(dropdownClassName)}>
              {(mid: Mid) => (
                <AutocompleteItem
                  key={mid.id}
                  value={mid}
                  className={cn(itemClassName)}
                  onClick={() => {
                    handleSelectMid(mid)
                  }}
                >
                  <div className='flex items-center gap-2.5 truncate'>
                    <div className='min-w-0 flex-1'>
                      <div
                        className='truncate font-medium'
                        dangerouslySetInnerHTML={{
                          __html: `${mid._highlighted?.brand ?? mid.brand}${
                            (mid._highlighted?.model ?? mid.model)
                              ? ` - ${mid._highlighted?.model ?? mid.model ?? ''}`
                              : ''
                          }`
                        }}
                      />
                    </div>
                  </div>
                </AutocompleteItem>
              )}
            </AutocompleteList>

            <AutocompleteStatus>
              <Button
                variant='ghost'
                className='w-full'
                size='sm'
                onClick={handleCreateMid}
              >
                <Plus className='mr-2 size-4' />
                Toevoegen "{searchValue}"
              </Button>
            </AutocompleteStatus>
          </AutocompleteContent>
        )}
      </Autocomplete>
    </div>
  )
}
